import { useNavigate } from 'react-router-dom'
import { store } from '@/store/store'
import { useCreateNewAgentChat } from '@/agent/conversations/hooks/useNewAgentChat'
import { selectLastViewedConversationId, selectMostRecentNewChat } from '../store/selectors'
import { selectAgentConversationFromId } from '@/agent/conversations/store/selectors'
import { buildConversationPath } from '../util/conversation-path-builders'
import { selectConversationsByMatter } from '@/chat-common/conversation_list/selectors'
import { useCallback } from 'react'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { getAuth } from 'firebase/auth'

/**
 * Use Conversation Navigation
 *
 * Hook for navigating between conversations based on the current matter context.
 * Handles restoring last viewed conversations, navigating to existing chats, or creating new ones as needed.
 */
export function useConversationNavigation() {
  const navigate = useNavigate()

  const { createNewAgentChat } = useCreateNewAgentChat()

  /**
   * Navigates to the appropriate conversation based on the current matter context.
   *
   * Selection logic priority:
   * 1. Navigates to the last viewed conversation for the given matter (if available).
   * 2. Navigates to the current user's most recent "New Chat" placeholder for the matter (if it exists).
   * 3. Navigates to the most recently active conversation (based on timestamp) for the matter.
   * 4. If no existing conversation is found, creates a new conversation and navigates to it.
   *
   * Steps 2 and 3 exclude other members' empty "New Chat" placeholders, so navigation never
   * lands you on someone else's empty chat.
   *
   * @param matterId Matter to navigate to (required)
   */
  const navigateToConversationByMatter = useCallback(
    ({ matterId }: { matterId: string | null }) => {
      const state = store.getState()
      // Read the uid fresh from auth (not AuthContext) to match how this callback reads all its
      // state via store.getState(), keeping it stable rather than re-created on every auth change.
      const currentUserId = getAuth().currentUser?.uid

      // No matter selected = can't navigate to conversations
      if (!matterId) return

      // Get the last viewed conversation for this matter
      const lastViewedConvoId = selectLastViewedConversationId(state, { matterId: matterId })
      const lastViewedConvo = lastViewedConvoId ? selectAgentConversationFromId(state, { conversationId: lastViewedConvoId }) : null

      // If there is a last viewed conversation for this matter, navigate to it
      // Use the passed-in matterId (not Redux activeMatterId) since Redux may not be hydrated yet on initial load
      if (lastViewedConvo && lastViewedConvo.matter_id === matterId) {
        navigate(buildConversationPath(matterId, lastViewedConvo.id))
        return
      }

      // Check if there's an existing "New Chat" for this matter
      const existingNewChat = selectMostRecentNewChat(state, { matterId: matterId, currentUserId })

      // If there's already a "New Chat" for this matter, navigate to it
      if (existingNewChat) {
        navigate(buildConversationPath(matterId, existingNewChat.id))
        return
      }

      // If there's no existing new chat, just navigate to the most recent conversation
      const conversations = selectConversationsByMatter(state, { matterId: matterId, currentUserId })
      if (conversations.length > 0) {
        navigate(buildConversationPath(matterId, conversations[0].id))
        return
      }

      // If no conversations exist for this matter, create a new one.
      // Don't attempt creation if the matter is the legacy unassigned matter.
      const matter = selectMatterById(state, { matterId })
      if (isUnassignedMatter(matter)) {
        return
      }

      createNewAgentChat(true, matterId)
    },
    [navigate, createNewAgentChat]
  )

  /**
   * Handles conversation navigation when switching matters
   *
   * @param targetMatterId The matter ID to switch to
   */
  const handleMatterChangeNavigation = useCallback(
    (targetMatterId: string | null) => {
      // Navigate to the correct conversation for the matter based on the navigation priority logic
      navigateToConversationByMatter({ matterId: targetMatterId })
    },
    [navigateToConversationByMatter]
  )

  return {
    navigateToConversationByMatter,
    handleMatterChangeNavigation,
  }
}
